// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/auth.service';
import { AuthState, User, LoginRequest, RegisterRequest } from '../types';

interface AuthContextType extends AuthState {
    login: (credentials: LoginRequest) => Promise<void>;
    register: (data: RegisterRequest) => Promise<void>;
    logout: () => void;
    updateUser: (user: User) => void;
    refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = useState<AuthState>({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
        isLoading: true,
        error: null,
    });

    useEffect(() => {
        const clearSession = () => setState({
            isAuthenticated: false, user: null, accessToken: null, refreshToken: null,
            isLoading: false, error: null,
        });
        const updateTokens = () => setState((current) => current.isAuthenticated ? {
            ...current,
            accessToken: authService.getAccessToken(),
            refreshToken: authService.getRefreshToken(),
        } : current);
        const syncStorage = (event: StorageEvent) => {
            if (event.key !== null && !['access_token', 'refresh_token', 'user_data'].includes(event.key)) return;
            const accessToken = authService.getAccessToken();
            const user = authService.getUser();
            if (!accessToken || !user) {
                clearSession();
                return;
            }
            setState({
                isAuthenticated: true, user, accessToken, refreshToken: authService.getRefreshToken(),
                isLoading: false, error: null,
            });
        };
        window.addEventListener('auth:logout', clearSession);
        window.addEventListener('auth:tokens-updated', updateTokens);
        window.addEventListener('storage', syncStorage);
        return () => {
            window.removeEventListener('auth:logout', clearSession);
            window.removeEventListener('auth:tokens-updated', updateTokens);
            window.removeEventListener('storage', syncStorage);
        };
    }, []);

    useEffect(() => {
        if (!state.isAuthenticated || !state.accessToken) return;
        const token = state.accessToken;
        let expiresAt: number;
        try {
            const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
            const decoded = JSON.parse(window.atob(payload));
            if (typeof decoded.exp !== 'number') return;
            expiresAt = decoded.exp * 1000;
        } catch {
            return;
        }
        let active = true;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const refreshIfNeeded = () => {
            if (!active || authService.getAccessToken() !== token) return;
            if (expiresAt - Date.now() > 60000) {
                schedule();
                return;
            }
            void authService.refreshToken().catch(() => {
                // Retry temporary network failures without discarding the session.
                if (active) {
                    clearTimeout(timer);
                    timer = setTimeout(refreshIfNeeded, 30000);
                }
            });
        };
        const schedule = () => {
            clearTimeout(timer);
            timer = setTimeout(refreshIfNeeded, Math.min(2147483647, Math.max(1000, expiresAt - Date.now() - 60000)));
        };
        schedule();
        window.addEventListener('focus', refreshIfNeeded);
        return () => {
            active = false;
            clearTimeout(timer);
            window.removeEventListener('focus', refreshIfNeeded);
        };
    }, [state.isAuthenticated, state.accessToken]);

    useEffect(() => {
        const initAuth = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const refreshToken = localStorage.getItem('refresh_token');
                const userStr = localStorage.getItem('user_data');

                console.log('🔍 Auth init:', { token: !!token, userStr, userStrType: typeof userStr });

                // ✅ Kiểm tra userStr không null, không undefined, không phải "undefined"
                if (token && userStr && userStr !== 'undefined' && userStr !== 'null') {
                    try {
                        const user = JSON.parse(userStr);
                        console.log('✅ User found:', user);

                        setState({
                            isAuthenticated: true,
                            user,
                            accessToken: token,
                            refreshToken: refreshToken,
                            isLoading: false,
                            error: null,
                        });
                    } catch (parseError) {
                        console.error('❌ Error parsing user_data:', parseError);
                        // Nếu parse lỗi, xóa dữ liệu và logout
                        localStorage.removeItem('user_data');
                        localStorage.removeItem('access_token');
                        localStorage.removeItem('refresh_token');
                        setState(prev => ({ ...prev, isLoading: false }));
                    }
                } else {
                    console.log('❌ No valid auth data found');
                    // Xóa dữ liệu không hợp lệ
                    if (userStr === 'undefined' || userStr === 'null') {
                        localStorage.removeItem('user_data');
                    }
                    setState(prev => ({ ...prev, isLoading: false }));
                }
            } catch (error) {
                console.error('❌ Auth init error:', error);
                setState({
                    isAuthenticated: false,
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isLoading: false,
                    error: 'Session expired',
                });
            }
        };

        initAuth();
    }, []);

    const login = async (credentials: LoginRequest) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await authService.login(credentials);

            let user: User | null = response.user ?? null;
            if (!user) {
                const savedUser = localStorage.getItem('user_data');
                if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
                    try {
                        user = JSON.parse(savedUser) as User;
                    } catch {
                        user = null;
                    }
                }
            }

            setState({
                isAuthenticated: true,
                user,
                accessToken: response.accessToken,
                refreshToken: response.refreshToken,
                isLoading: false,
                error: null,
            });

            const saved = localStorage.getItem('user_data');
            console.log('📦 user_data after login:', saved);

        } catch (error: any) {
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: error.message || 'Login failed',
            }));
            throw error;
        }
    };

    const register = async (data: RegisterRequest) => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const response = await authService.register(data);
            setState({
                isAuthenticated: true,
                user: response.user,
                accessToken: response.accessToken,
                refreshToken: response.refreshToken,
                isLoading: false,
                error: null,
            });
        } catch (error: any) {
            setState(prev => ({
                ...prev,
                isLoading: false,
                error: error.message || 'Registration failed',
            }));
            throw error;
        }
    };

    const logout = () => {
        console.log('🔴 Logging out...');
        authService.logout();

        setState({
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            isLoading: false,
            error: null,
        });
    };

    const updateUser = (user: User) => {
        setState(prev => ({ ...prev, user }));
        localStorage.setItem('user_data', JSON.stringify(user));
    };

    const refreshAuth = async () => {
        try {
            const token = await authService.refreshToken();
            if (token) {
                localStorage.setItem('access_token', token);
                setState(prev => ({ ...prev, accessToken: token }));
            }
        } catch (error) {
            setState(prev => ({ ...prev, error: 'Không thể làm mới phiên đăng nhập. Vui lòng thử lại.' }));
        }
    };

    return (
        <AuthContext.Provider value={{
            ...state,
            login,
            register,
            logout,
            updateUser,
            refreshAuth,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
